/**
 * Admin Tier Management Page
 *
 * View and edit subscription tier enforcement limits directly.
 * Values are stored in the `tier_feature_config` database table
 * and take effect immediately (no code deployment required).
 *
 * Backend API: routers/admin_tier_config.py
 * Database: tier_feature_config (migration 200)
 *
 * Mission 33 Sprint 7 – Path A (Feb 2026)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FreeMonthBackfillPanel } from '../components/admin/FreeMonthBackfillPanel';
import {
  RefreshCw,
  Save,
  Check,
  AlertTriangle,
  Shield,
  BarChart3,
  ToggleLeft,
  ToggleRight,
  Info,
  Clock,
} from 'lucide-react';
import { API_BASE_URL } from '../utils/apiConfig';
import { storage } from '../services/storage';
import { formatPrice, getTierConfig } from '../config/subscriptionTiers';
import { RcCohortLabelsPanel } from '../components/admin/RcCohortLabelsPanel';
import { ReadOnlyNote, useAdminRole } from './admin/partners/PartnersShared';

// ── Types ────────────────────────────────────────────────────────────────────

interface FeatureConfig {
  value: any;
  description: string | null;
  updated_at: string | null;
}

interface TierData {
  features: Record<string, FeatureConfig>;
  feature_count: number;
}

interface TierConfigResponse {
  tiers: Record<string, TierData>;
  total_features: number;
}

interface EditState {
  [key: string]: string; // "tier:feature_key" → edited value string
}

interface SaveStatus {
  [key: string]: 'saving' | 'saved' | 'error';
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const TIER_ORDER = ['trial', 'full', 'extended'] as const;

/** A paid tier's list prices, from the app's tier config — the same numbers as the
 *  backend's services/tier_pricing.py (tests/gate/test_app_tier_prices_match_backend.py),
 *  never a copy typed here (they read $29.99/$49.99 for months after the prices moved). */
function priceTagline(tier: 'full' | 'extended'): string {
  const { monthly, annual } = getTierConfig(tier).pricing;
  return annual > 0 ? `${formatPrice(monthly, 'monthly')} · ${formatPrice(annual, 'annual')}` : formatPrice(monthly, 'monthly');
}

const TIER_META: Record<string, { label: string; color: string; bgColor: string; borderColor: string; emoji: string; tagline: string }> = {
  trial: {
    label: 'Trial',
    color: '#6B7280',
    bgColor: '#F9FAFB',
    borderColor: '#E5E7EB',
    emoji: '🧪',
    tagline: '7-day free trial',
  },
  full: {
    label: 'Full',
    color: '#1F6C4F',
    bgColor: '#F0FDF4',
    borderColor: '#BBF7D0',
    emoji: '🌲',
    tagline: priceTagline('full'),
  },
  extended: {
    label: 'Extended',
    color: '#7C3AED',
    bgColor: '#FAF5FF',
    borderColor: '#DDD6FE',
    emoji: '🚀',
    tagline: priceTagline('extended'),
  },
};

function isEnforced(description: string | null): boolean {
  if (!description) return false;
  return description.includes('ENFORCED');
}

function isBoolean(val: any): boolean {
  return val === true || val === false || val === 'true' || val === 'false';
}

function parseBool(val: any): boolean {
  if (typeof val === 'boolean') return val;
  return val === 'true';
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function featureDisplayName(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Component ────────────────────────────────────────────────────────────────

export function AdminTierManagement() {
  // A tier's features are paid value for everyone on it: changing one is admin / super admin
  // only (the server answers 403 to support and viewer — Codes PR-1b, L4). Their sessions see the
  // values, with no toggle, field or Save (review N-e). Fail closed while the role is unknown.
  const { role, writable, loaded: roleLoaded } = useAdminRole();
  const [tierConfig, setTierConfig] = useState<TierConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({});
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({});
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());

  // ── Fetch ────────────────────────────────────────────────────────────────

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await storage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/api/admin/tier-config/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data: TierConfigResponse = await res.json();
      setTierConfig(data);
      setLastRefreshed(new Date());
      // Clear edits on fresh fetch
      setEditState({});
      setSaveStatus({});
    } catch (err: any) {
      setError(err.message || 'Failed to load tier configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // ── Save ─────────────────────────────────────────────────────────────────

  const saveFeature = async (tierName: string, featureKey: string, newValue: string) => {
    const compositeKey = `${tierName}:${featureKey}`;
    setSaveStatus((prev) => ({ ...prev, [compositeKey]: 'saving' }));

    try {
      const token = await storage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/api/admin/tier-config/update`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          tier_name: tierName,
          feature_key: featureKey,
          feature_value: newValue,
          updated_by: 'admin-portal',
        }),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${res.status}`);
      }

      setSaveStatus((prev) => ({ ...prev, [compositeKey]: 'saved' }));

      // Update local state to reflect new value
      setTierConfig((prev) => {
        if (!prev) return prev;
        const updated = { ...prev };
        const tier = updated.tiers[tierName];
        if (tier && tier.features[featureKey]) {
          // Parse the value the same way the backend does
          let parsed: any = newValue;
          try { parsed = JSON.parse(newValue); } catch { /* keep as string */ }
          tier.features[featureKey] = {
            ...tier.features[featureKey],
            value: parsed,
            updated_at: new Date().toISOString(),
          };
        }
        return updated;
      });

      // Clear edit state for this field
      setEditState((prev) => {
        const copy = { ...prev };
        delete copy[compositeKey];
        return copy;
      });

      // Clear saved status after 3 seconds
      setTimeout(() => {
        setSaveStatus((prev) => {
          const copy = { ...prev };
          delete copy[compositeKey];
          return copy;
        });
      }, 3000);
    } catch (err: any) {
      setSaveStatus((prev) => ({ ...prev, [compositeKey]: 'error' }));
      console.error(`Failed to save ${compositeKey}:`, err);
    }
  };

  const handleToggle = async (tierName: string, featureKey: string, currentValue: boolean) => {
    const newVal = (!currentValue).toString();
    await saveFeature(tierName, featureKey, newVal);
  };

  // ── Render helpers ───────────────────────────────────────────────────────

  const renderFeatureRow = (tierName: string, featureKey: string, feature: FeatureConfig) => {
    const compositeKey = `${tierName}:${featureKey}`;
    const enforced = isEnforced(feature.description);
    const isBool = isBoolean(feature.value);
    const currentEditValue = editState[compositeKey];
    const status = saveStatus[compositeKey];
    const hasUnsavedEdit = currentEditValue !== undefined && currentEditValue !== String(feature.value);

    return (
      <div
        key={compositeKey}
        className="flex items-center justify-between py-3 px-4 rounded-lg transition-colors border"
        style={{
          backgroundColor: enforced ? '#E8F2ED' : '#F9FAFB',
          borderColor: enforced ? '#C6DDD3' : '#F3F4F6',
        }}
      >
        {/* Left: label + badge */}
        <div className="flex-1 min-w-0 mr-4">
          <div className="flex items-center gap-2">
            {enforced ? (
              <Shield className="h-4 w-4 flex-shrink-0" style={{ color: '#1F6C4F' }} />
            ) : (
              <BarChart3 className="h-4 w-4 text-gray-400 flex-shrink-0" />
            )}
            <span className="font-medium text-sm text-gray-900 truncate">
              {featureDisplayName(featureKey)}
            </span>
            <span
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0"
              style={enforced
                ? { backgroundColor: '#E8F2ED', color: '#1F6C4F' }
                : { backgroundColor: '#E5E7EB', color: '#6B7280' }
              }
            >
              {enforced ? 'ENFORCED' : 'MODELING'}
            </span>
          </div>
          {feature.description && (
            <p className="text-xs text-gray-500 mt-0.5 ml-6 truncate" title={feature.description}>
              {feature.description.replace(/^[✅📊]\s*[A-Z]+:\s*/, '')}
            </p>
          )}
          {feature.updated_at && (
            <p className="text-[10px] text-gray-400 mt-0.5 ml-6 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatTimestamp(feature.updated_at)}
            </p>
          )}
        </div>

        {/* Right: value control (plain text for a session that cannot write) */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {!writable ? (
            <span className="text-sm text-gray-700" data-testid="tier-feature-readonly">
              {isBool ? (parseBool(feature.value) ? 'On' : 'Off') : String(feature.value)}
            </span>
          ) : isBool ? (
            <button
              onClick={() => handleToggle(tierName, featureKey, parseBool(feature.value))}
              className="focus:outline-none"
              title={parseBool(feature.value) ? 'Enabled' : 'Disabled'}
            >
              {parseBool(feature.value) ? (
                <ToggleRight className="h-7 w-7" style={{ color: '#1F6C4F' }} />
              ) : (
                <ToggleLeft className="h-7 w-7 text-gray-400" />
              )}
            </button>
          ) : (
            <>
              <input
                type="text"
                className={`w-20 px-2 py-1 text-sm text-right rounded-md focus:outline-none focus:ring-2 ${
                  enforced ? 'font-semibold' : ''
                }`}
                style={{
                  border: `1px solid ${enforced ? '#C6DDD3' : '#D1D5DB'}`,
                }}
                value={currentEditValue ?? String(feature.value)}
                onChange={(e) =>
                  setEditState((prev) => ({ ...prev, [compositeKey]: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && hasUnsavedEdit) {
                    saveFeature(tierName, featureKey, currentEditValue!);
                  }
                }}
              />
              {hasUnsavedEdit && (
                <button
                  onClick={() => saveFeature(tierName, featureKey, currentEditValue!)}
                  className="p-1 rounded-md text-white transition-colors"
                  style={{ backgroundColor: '#1F6C4F' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#113C2B'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1F6C4F'; }}
                  title="Save change"
                >
                  <Save className="h-3.5 w-3.5" />
                </button>
              )}
            </>
          )}

          {/* Status indicator */}
          {status === 'saving' && (
            <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />
          )}
          {status === 'saved' && (
            <Check className="h-4 w-4" style={{ color: '#1F6C4F' }} />
          )}
          {status === 'error' && (
            <AlertTriangle className="h-4 w-4 text-red-500" aria-label="Save failed – try again" />
          )}
        </div>
      </div>
    );
  };

  const renderTierCard = (tierName: string, tierData: TierData) => {
    const meta = TIER_META[tierName] || {
      label: tierName,
      color: '#6B7280',
      bgColor: '#F9FAFB',
      borderColor: '#E5E7EB',
      emoji: '📦',
      tagline: '',
    };

    // Separate enforced vs modeling features
    const enforced: [string, FeatureConfig][] = [];
    const modeling: [string, FeatureConfig][] = [];

    Object.entries(tierData.features).forEach(([key, feat]) => {
      if (isEnforced(feat.description)) {
        enforced.push([key, feat]);
      } else {
        modeling.push([key, feat]);
      }
    });

    return (
      <div
        key={tierName}
        className="bg-white overflow-hidden"
        style={{ borderRadius: '14px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}
      >
        {/* Card header */}
        <div
          className="px-6 py-4 border-b"
          style={{ backgroundColor: meta.bgColor, borderColor: meta.borderColor }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold" style={{ color: meta.color }}>
                {meta.emoji} {meta.label}
              </h3>
              <p className="text-sm text-gray-500">{meta.tagline}</p>
            </div>
            <div className="text-xs text-gray-400 bg-white px-2.5 py-1 rounded-full border" style={{ borderColor: meta.borderColor }}>
              {tierData.feature_count} features
            </div>
          </div>
        </div>

        {/* Enforced section */}
        {enforced.length > 0 && (
          <div className="px-6 pt-4 pb-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: '#1F6C4F' }}>
              <Shield className="h-3.5 w-3.5" />
              Enforced Limits
            </h4>
            <div className="space-y-2">
              {enforced.map(([key, feat]) => renderFeatureRow(tierName, key, feat))}
            </div>
          </div>
        )}

        {/* Modeling section */}
        {modeling.length > 0 && (
          <div className="px-6 pt-4 pb-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <BarChart3 className="h-3.5 w-3.5" />
              Modeling &amp; Toggles
            </h4>
            <div className="space-y-2">
              {modeling.map(([key, feat]) => renderFeatureRow(tierName, key, feat))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Main render ──────────────────────────────────────────────────────────

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: '#0B1614' }}>Subscription Tier Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Edit enforcement limits per tier. Changes take effect immediately — no deployment needed.
          </p>
        </div>
        <button
          onClick={fetchConfig}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <ReadOnlyNote writable={writable} loaded={roleLoaded} role={role} subject="tier features" />

      {/* Info banner */}
      <div className="mb-6 p-4 rounded-lg flex items-start gap-3" style={{ backgroundColor: '#E8F2ED', border: '1px solid #C6DDD3' }}>
        <Info className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#1F6C4F' }} />
        <div className="text-sm" style={{ color: '#113C2B' }}>
          <strong>How this works:</strong>{' '}
          <span className="font-semibold" style={{ color: '#1F6C4F' }}>ENFORCED</span> values are hard limits checked
          on every chat message (currently <code className="px-1 rounded" style={{ backgroundColor: '#D4E8DC' }}>weekly_chat_limit</code> only).{' '}
          <span className="font-semibold text-gray-500">MODELING</span> values are for cost forecasting
          and are <em>not</em> enforced for paid tiers. To add a new tier, create products in
          App Store / Play Store, then add an entitlement in RevenueCat, and finally seed rows here.
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {/* Loading */}
      {loading && !tierConfig && (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
          <span className="ml-3 text-gray-500">Loading tier configuration...</span>
        </div>
      )}

      {/* Tier cards */}
      {tierConfig && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {TIER_ORDER.map((tierName) => {
            const data = tierConfig.tiers[tierName];
            if (!data) return null;
            return renderTierCard(tierName, data);
          })}
        </div>
      )}

      {/* Clear ended free months for paying users: preview, then apply (super_admin; J24) */}
      <FreeMonthBackfillPanel />

      {/* Footer / last refreshed */}
      {tierConfig && (
        <div className="mt-6 text-xs text-gray-400 text-center">
          Last refreshed: {formatTimestamp(lastRefreshed.toISOString())} · Source: <code>tier_feature_config</code> table
        </div>
      )}

      {/* RevenueCat cohort labels: preview, then apply (super_admin; #3967) */}
      <RcCohortLabelsPanel />
    </div>
  );
}

export default AdminTierManagement;
